import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';
import type { Params } from '@angular/router';
import { RouterModule } from '@angular/router';

import type { LinkTarget } from '../../core';
import { toQueryString } from '../../core';
import { IsExternalPipe } from '../../pipes/link-parser/is-external.pipe';
import {
	LinkParsePipe,
	parseLink,
} from '../../pipes/link-parser/link-parser.pipe';

// Sentinel base so relative paths can be parsed by `new URL`; stripped
// again when serializing, keeping internal hrefs relative to the domain.
const RELATIVE_URL_BASE = 'https://relative-url.invalid';

@Component({
	selector: 'valk-core-smart-link',
	imports: [RouterModule, IsExternalPipe, LinkParsePipe, CommonModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<ng-template #contentTpl><ng-content /></ng-template>

		@let href = url();

		@if (linkType() === 'email') {
			<a
				[attr.data-test-id]="testId()"
				[class]="className()"
				[href]="'mailto:' + href"
			>
				<ng-container [ngTemplateOutlet]="contentTpl" />
			</a>
		} @else if (!forceReload() && !(href | isExternal)) {
			<a
				[attr.data-test-id]="testId()"
				[class]="className()"
				[queryParams]="queryParams()"
				[routerLink]="[href | linkparse]"
				[target]="target()"
			>
				<ng-container [ngTemplateOutlet]="contentTpl" />
			</a>
		} @else {
			<a
				[attr.data-test-id]="testId()"
				[class]="className()"
				[href]="resolvedHref()"
				[rel]="rel() ?? 'noopener noreferrer'"
				[target]="target()"
			>
				<ng-container [ngTemplateOutlet]="contentTpl" />
			</a>
		}
	`,
})
export class SmartLinkComponent {
	readonly url = input.required<string>();
	readonly queryParams = input<Params>();
	readonly className = input<string | undefined>(undefined);
	readonly testId = input<string | undefined>(undefined);
	readonly linkType = input<string | undefined>(undefined);
	readonly forceReload = input<boolean>(false);

	readonly target = input<LinkTarget | undefined>(undefined);
	readonly rel = input<string | undefined>(undefined);

	readonly resolvedHref = computed<string>(() => {
		if (!this.forceReload()) {
			return this.url();
		}

		const href = new URL(parseLink(this.url()), RELATIVE_URL_BASE);
		const search = toQueryString(this.queryParams());

		for (const [key, value] of new URLSearchParams(search)) {
			href.searchParams.set(key, value);
		}

		return href.origin === RELATIVE_URL_BASE
			? href.pathname + href.search + href.hash
			: href.href;
	});
}
